// @ts-nocheck
/*
 * Bulk Voice Upload — create multiple prompts (each with its own
 * name/voice-type/language+file rows) in a single request.
 *
 * Talks to POST /prompt/bulk (API_BULK_CREATE_PROMPTS):
 *   - `meta` field: JSON string, array of
 *       { prompt_name, voice_type, languages: [code, ...] }
 *   - files: one multipart field per language, named `file_<promptIndex>_<langIndex>`
 *     matching the order of that prompt's `languages` array.
 *   - response: { created: string[], failed: [{ prompt_name, reason }] }
 *
 * Mirrors AddVoicePage's per-row validation (filename regex, one file per
 * language, no duplicate languages within a prompt) but repeated per prompt
 * block, plus a client-side duplicate-name guard across blocks in the batch.
 *
 * UX: a prompt block starts "expanded" (full editable card). Clicking
 * "Add prompt" validates + collapses the currently expanded block(s) into a
 * summary table row (name / voice type / languages / file count) with
 * Edit/Delete actions, then opens a fresh expanded block. Edit re-expands a
 * row back into the full card for editing.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Upload as UploadIcon,
  Trash2,
  Plus,
  FileAudio,
  ArrowLeft,
  Loader2,
  Layers,
  Pencil,
  Play,
  Square,
  Check,
  Globe,
  CheckCircle2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiCall, postApiMediaCall } from '@/service/httpCall';
import { API_BULK_CREATE_PROMPTS } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { filterLanguagesByAcl, filterVoiceTypesByAcl } from '@/utilities/voiceLibraryAcl';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

// Shows a compact "Label: count" chip; clicking opens a modal with the
// full list.
const ListCell = ({
  label,
  title,
  items,
  icon: Icon,
  layout = 'list',
}: {
  label: string;
  title: string;
  items: React.ReactNode[];
  icon: any;
  layout?: 'list' | 'wrap';
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15 active:scale-[0.97]"
      >
        <Icon className="h-3 w-3" />
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">None</p>
          ) : layout === 'wrap' ? (
            <div className="flex flex-wrap gap-2">
              {items.map((item, i) => (
                <React.Fragment key={i}>{item}</React.Fragment>
              ))}
            </div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-1.5 text-foreground"
                >
                  {item}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

let blockIdSeq = 1;
let pairIdSeq = 1;

const newPair = () => ({ id: pairIdSeq++, language: '', file: null });
const newBlock = () => ({
  id: blockIdSeq++,
  fileName: '',
  fileNameError: '',
  voiceType: '',
  collapsed: false,
  langFilePairs: [newPair()],
});

const validateFileName = (name: string) => {
  if (!name.trim()) return 'File name is required';
  if (name.includes(' ')) return 'File name cannot contain spaces';
  if (!NAME_REGEX.test(name)) {
    return 'File name can only contain letters, numbers, underscore (_), and hyphen (-)';
  }
  return '';
};

const BulkAddVoice = () => {
  const navigate = useNavigate();
  const [allLanguages, setAllLanguages] = useState<any[]>([]);
  const [moduleOperations, setModuleOperations] = useState<any>({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const languages = useMemo(
    () => filterLanguagesByAcl(moduleOperations, isSuperAdmin, allLanguages),
    [moduleOperations, isSuperAdmin, allLanguages],
  );
  const [allowedVoiceTypes, setAllowedVoiceTypes] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([newBlock()]);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showResultModal, setShowResultModal] = useState(false);

  const [playingKey, setPlayingKey] = useState<any>(null);
  const audioRef = useRef<any>(null);
  const audioUrlRef = useRef<any>(null);

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setPlayingKey(null);
  };

  const togglePreview = (pair: any, key: string) => {
    if (!pair.file) return;
    if (playingKey === key) {
      stopPreview();
      return;
    }
    stopPreview();
    const url = URL.createObjectURL(pair.file);
    const audio = new Audio(url);
    audio.onended = stopPreview;
    audio.onerror = () => {
      toast.error('Unable to play selected audio.');
      stopPreview();
    };
    audioRef.current = audio;
    audioUrlRef.current = url;
    audio
      .play()
      .then(() => setPlayingKey(key))
      .catch(() => stopPreview());
  };

  useEffect(() => () => stopPreview(), []);

  useEffect(() => {
    const resp = getUser();
    if (resp) {
      const superAdmin = resp.role === USER_ROLES.SUPERADMIN;
      setIsSuperAdmin(superAdmin);
      if (resp?.acl_settings) {
        const ops = parseModuleOperations(resp.acl_settings.module_operations);
        setModuleOperations(ops);
        setAllowedVoiceTypes(filterVoiceTypesByAcl(ops, superAdmin));
      } else if (superAdmin) {
        setAllowedVoiceTypes(filterVoiceTypesByAcl(null, true));
      }
    }
    getApiCall('/v2/tts-languages').then((response: any) => {
      if (Array.isArray(response)) {
        setAllLanguages(response.map((lang: any) => ({ label: lang.language, value: lang.code })));
      }
    });
  }, []);

  const voiceTypeOptions =
    allowedVoiceTypes.length > 0
      ? allowedVoiceTypes
      : filterVoiceTypesByAcl(moduleOperations, isSuperAdmin);

  const getBlockError = (block: any, allBlocks: any[]) => {
    const nameErr = validateFileName(block.fileName);
    if (nameErr) return `"${block.fileName || 'Untitled'}": ${nameErr}`;
    const lowerName = block.fileName.trim().toLowerCase();
    const isDuplicate = allBlocks.some(
      (b: any) => b.id !== block.id && b.fileName.trim().toLowerCase() === lowerName,
    );
    if (isDuplicate) return `Duplicate prompt name in this batch: "${block.fileName}"`;
    if (!block.voiceType) return `Select a voice type for "${block.fileName}".`;
    if (block.langFilePairs.length === 0) {
      return `"${block.fileName}" needs at least one language/file row.`;
    }
    for (const pair of block.langFilePairs) {
      if (!pair.language) return `"${block.fileName}": select a language for every row.`;
      if (!pair.file) {
        const langLabel = languages.find((l: any) => l.value === pair.language)?.label || pair.language;
        return `"${block.fileName}": select a file for language "${langLabel}".`;
      }
    }
    return '';
  };

  const addBlock = () => {
    setBlocks((prev) => [...prev, newBlock()]);
  };

  const saveBlock = (id: any) => {
    const block = blocks.find((b: any) => b.id === id);
    if (!block) return;
    const err = getBlockError(block, blocks);
    if (err) {
      toast.error(err);
      return;
    }
    updateBlock(id, { collapsed: true });
  };

  const editBlock = (id: any) => {
    setBlocks((prev) => prev.map((b: any) => (b.id === id ? { ...b, collapsed: false } : b)));
  };

  const removeBlock = (id: any) => {
    if (playingKey && String(playingKey).startsWith(`${id}:`)) stopPreview();
    setBlocks((prev) => prev.filter((b: any) => b.id !== id));
  };

  const updateBlock = (id: any, patch: any) => {
    setBlocks((prev) => prev.map((b: any) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const handleFileNameChange = (id: any, value: string) => {
    updateBlock(id, { fileName: value, fileNameError: value.trim() ? validateFileName(value) : '' });
  };

  const addPair = (blockId: any) => {
    setBlocks((prev) =>
      prev.map((b: any) =>
        b.id === blockId ? { ...b, langFilePairs: [...b.langFilePairs, newPair()] } : b,
      ),
    );
  };

  const removePair = (blockId: any, pairId: any) => {
    const key = `${blockId}:${pairId}`;
    if (playingKey === key) stopPreview();
    setBlocks((prev) =>
      prev.map((b: any) =>
        b.id === blockId
          ? { ...b, langFilePairs: b.langFilePairs.filter((p: any) => p.id !== pairId) }
          : b,
      ),
    );
  };

  const updatePair = (blockId: any, pairId: any, patch: any) => {
    const key = `${blockId}:${pairId}`;
    if (playingKey === key) stopPreview();
    setBlocks((prev) =>
      prev.map((b: any) =>
        b.id === blockId
          ? {
              ...b,
              langFilePairs: b.langFilePairs.map((p: any) =>
                p.id === pairId ? { ...p, ...patch } : p,
              ),
            }
          : b,
      ),
    );
  };

  const validateBatch = () => {
    if (blocks.length === 0) {
      toast.error('Add at least one prompt to upload.');
      return false;
    }
    for (const block of blocks) {
      const err = getBlockError(block, blocks);
      if (err) {
        toast.error(err);
        return false;
      }
    }
    return true;
  };

  // Partial success: whatever passed validation is already saved, only the
  // failed ones are reported here. Closing the modal goes to the listing
  // page — re-editing this same form would just re-collide with the
  // prompts that already succeeded ("File Name Already Exists") on retry.
  const closeResultModal = () => {
    setShowResultModal(false);
    navigate(`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`);
  };

  const handleBulkUpload = async () => {
    if (!validateBatch()) return;
    setIsUploading(true);
    setResult(null);
    try {
      const meta = blocks.map((block) => ({
        prompt_name: block.fileName,
        voice_type: block.voiceType,
        languages: block.langFilePairs.map((p: any) => p.language),
      }));
      const formData = new FormData();
      formData.append('meta', JSON.stringify(meta));
      blocks.forEach((block, i) => {
        block.langFilePairs.forEach((pair: any, j: number) => {
          formData.append(`file_${i}_${j}`, pair.file);
        });
      });
      const response: any = await postApiMediaCall(API_BULK_CREATE_PROMPTS, formData);
      setResult(response);
      const createdCount = response?.created?.length || 0;
      const failedCount = response?.failed?.length || 0;

      // AddVoicePage's success text differs by voice type (incoming = live
      // immediately, outgoing/both = needs admin approval) — a batch can
      // mix both, so summarize the split instead of picking just one line.
      const createdNames = new Set(response?.created || []);
      const createdBlocks = blocks.filter((b: any) => createdNames.has(b.fileName));
      const incomingCount = createdBlocks.filter((b: any) => b.voiceType === 'incoming').length;
      const pendingCount = createdBlocks.length - incomingCount; // outgoing + both

      let successMsg = `${createdCount} prompt${createdCount === 1 ? '' : 's'} uploaded successfully`;
      if (incomingCount > 0 && pendingCount > 0) {
        successMsg += ` — ${incomingCount} live now, ${pendingCount} pending admin approval`;
      } else if (pendingCount > 0) {
        successMsg += ', please wait for Admin Approval';
      }

      if (failedCount === 0 && createdCount > 0) {
        // Full success — same success-popup + auto-redirect pattern as the
        // single-upload page.
        setSuccessMessage(successMsg);
        setShowSuccessModal(true);
        setTimeout(() => navigate(`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`), 1500);
      } else {
        if (createdCount) toast.success(successMsg);
        if (failedCount) setShowResultModal(true);
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Bulk upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const collapsedBlocks = blocks.filter((b: any) => b.collapsed);
  const expandedBlocks = blocks.filter((b: any) => !b.collapsed);

  const langLabel = (code: string) => languages.find((l: any) => l.value === code)?.label || code;
  const voiceTypeLabel = (value: string) =>
    voiceTypeOptions.find((o: any) => o.value === value)?.label || value || '—';

  return (
    <FillPage>
      <PageHeader
        title="Bulk Upload Voice Files"
        description="Create multiple IVR prompts in one go — each prompt can carry its own languages and audio files."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow: '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-4 flex items-center justify-between gap-3 border-b border-border/50 pb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">Prompts in this batch</h3>
              <p className="text-[11px] text-muted-foreground">
                Add as many prompts as you need in this batch.
              </p>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addBlock} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Add prompt
          </Button>
        </div>

        {collapsedBlocks.length > 0 && (
          <div className="mb-4 overflow-x-auto rounded-md border border-border/60">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-3 py-2 font-semibold text-foreground">#</th>
                  <th className="px-3 py-2 font-semibold text-foreground">Prompt name</th>
                  <th className="px-3 py-2 font-semibold text-foreground">Voice type</th>
                  <th className="px-3 py-2 font-semibold text-foreground">Languages</th>
                  <th className="px-3 py-2 font-semibold text-foreground">Files</th>
                  <th className="px-3 py-2 text-right font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {collapsedBlocks.map((block: any, idx: number) => (
                  <tr key={block.id} className="border-t border-border/50">
                    <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium text-foreground">{block.fileName || '—'}</td>
                    <td className="px-3 py-2 text-foreground">{voiceTypeLabel(block.voiceType)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      <ListCell
                        icon={Globe}
                        layout="wrap"
                        label={`Languages: ${block.langFilePairs.length}`}
                        title={`Languages — ${block.fileName || 'Untitled'}`}
                        items={block.langFilePairs.map((p: any, i: number) => (
                          <span
                            key={i}
                            className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20"
                          >
                            {langLabel(p.language) || 'Not selected'}
                          </span>
                        ))}
                      />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      <ListCell
                        icon={FileAudio}
                        label={`Files: ${block.langFilePairs.filter((p: any) => p.file).length}`}
                        title={`Files — ${block.fileName || 'Untitled'}`}
                        items={block.langFilePairs.map((p: any, i: number) => (
                          <React.Fragment key={i}>
                            <span className="inline-flex flex-none items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
                              {langLabel(p.language) || 'Not selected'}
                            </span>
                            <span className="truncate">{p.file ? p.file.name : 'No file'}</span>
                          </React.Fragment>
                        ))}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => editBlock(block.id)}
                          title="Edit"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {blocks.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBlock(block.id)}
                            title="Delete"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="space-y-4">
          {expandedBlocks.map((block: any) => {
            const usedLanguages = block.langFilePairs.map((p: any) => p.language).filter(Boolean);
            return (
              <div key={block.id} className="rounded-xl border border-border/60 bg-muted/10 p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-foreground">
                      File name <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={block.fileName}
                      onChange={(e) => handleFileNameChange(block.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === ' ') e.preventDefault();
                      }}
                      placeholder="e.g. welcome-en-male"
                      className={`input-embossed h-10 w-full rounded-md px-3 text-sm ${
                        block.fileNameError ? 'ring-2 ring-destructive/40' : ''
                      }`}
                    />
                    {block.fileNameError && (
                      <p className="text-[11px] text-destructive">{block.fileNameError}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-foreground">
                      Voice type <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={block.voiceType}
                      onChange={(e) => updateBlock(block.id, { voiceType: e.target.value })}
                      className="input-embossed h-10 w-full rounded-md px-3 text-sm"
                    >
                      <option value="">Select voice type…</option>
                      {voiceTypeOptions.map((opt: any) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <h4 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Language &amp; file
                  </h4>
                  {block.langFilePairs.length < languages.length && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addPair(block.id)}
                      className="gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add language
                    </Button>
                  )}
                </div>

                <div className="mt-2 space-y-2">
                  {block.langFilePairs.map((pair: any) => {
                    const availableLangs = languages.filter(
                      (l: any) => l.value === pair.language || !usedLanguages.includes(l.value),
                    );
                    const previewKey = `${block.id}:${pair.id}`;
                    const isPlaying = playingKey === previewKey;
                    return (
                      <div key={pair.id} className="flex flex-wrap items-start gap-3 rounded-lg border border-border/50 bg-card p-2.5">
                        <div className="w-full space-y-1.5 sm:w-[200px]">
                          <select
                            value={pair.language}
                            onChange={(e) =>
                              updatePair(block.id, pair.id, { language: e.target.value, file: null })
                            }
                            className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
                          >
                            <option value="">Select language…</option>
                            {availableLangs.map((l: any) => (
                              <option key={l.value} value={l.value}>
                                {l.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {pair.language && (
                          <div className="min-w-0 flex-1 space-y-1.5">
                            <input
                              type="file"
                              accept=".wav,.mp3"
                              onChange={(e: any) =>
                                updatePair(block.id, pair.id, { file: e.target.files[0] || null })
                              }
                              className="input-embossed h-9 w-full rounded-md px-2 py-1 text-xs file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-primary hover:file:bg-primary/15"
                            />
                            {pair.file && (
                              <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-2 py-1">
                                <button
                                  type="button"
                                  onClick={() => togglePreview(pair, previewKey)}
                                  title={isPlaying ? 'Stop' : 'Listen'}
                                  className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
                                >
                                  {isPlaying ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                </button>
                                <FileAudio className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="truncate text-[11px] font-medium text-foreground">
                                  {pair.file.name}
                                </span>
                                <span className="ml-auto text-[10px] text-muted-foreground">
                                  {(pair.file.size / 1024).toFixed(0)} KB
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {block.langFilePairs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePair(block.id, pair.id)}
                            title="Remove language"
                            className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  {blocks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBlock(block.id)}
                      className="inline-flex items-center gap-1.5 rounded-md bg-rose-100 px-2.5 py-1 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Remove this prompt
                    </button>
                  )}
                  <Button type="button" size="sm" onClick={() => saveBlock(block.id)} className="gap-1.5">
                    <Check className="h-3.5 w-3.5" />
                    Add
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.VOICE_LIBRARY.VIEW_VOICE_FILES}`}>Cancel</Link>
          </Button>
          <Button type="button" onClick={handleBulkUpload} disabled={isUploading} className="gap-1.5">
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadIcon className="h-4 w-4" />}
            {isUploading ? 'Uploading…' : `Upload ${blocks.length} prompt${blocks.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>

      <Dialog
        open={showResultModal}
        onOpenChange={(o: boolean) => {
          if (!o) closeResultModal();
        }}
      >
        <DialogContent
          className="sm:max-w-lg"
          onInteractOutside={(e: any) => e.preventDefault()}
          onEscapeKeyDown={(e: any) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Bulk upload result</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 text-[11px]">
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
              Created: {result?.created?.length || 0}
            </span>
            <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
              Failed: {result?.failed?.length || 0}
            </span>
          </div>

          <div className="max-h-72 overflow-x-auto overflow-y-auto rounded-md border border-border/60">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-3 py-2 font-semibold text-foreground">Prompt name</th>
                  <th className="px-3 py-2 font-semibold text-foreground">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(result?.failed || []).map((f: any, idx: number) => (
                  <tr key={idx} className="border-t border-border/50">
                    <td className="px-3 py-2 text-foreground">{f.prompt_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{f.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button onClick={closeResultModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/30 dark:text-emerald-400">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h5 className="text-base font-semibold text-foreground">Upload successful</h5>
            <p className="text-sm text-muted-foreground">{successMessage}</p>
          </div>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default BulkAddVoice;
